import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useForm } from '@mantine/form'
import {
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Title,
  Box,
  Image,
  Grid,
  Container,
  LoadingOverlay,
} from '@mantine/core'
import { useAuthStore } from '../stores/auth'
import { getRedirectUrl } from '../utils/auth'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/login')({
  component: LoginComponent,
})

function LoginComponent() {
  const { login, isLoading, user } = useAuthStore()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const search = useSearch({ from: '/login' })
  const navigate = useNavigate()
  const { t } = useTranslation('login')

  const form = useForm({
    initialValues: {
      username: '',
      password: '',
    },
    validate: {
      username: value => (value.trim().length > 0 ? null : t('emailRequired')),
      password: value => (value.length < 6 ? t('passwordMinLength') : null),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    try {
      await login(values.username, values.password)
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  if (user && !isLoading) {
    const redirectUrl = getRedirectUrl(search)
    navigate({ to: redirectUrl, replace: true })
    return null
  }

  return (
    <Container fluid p={0} m={0}>
      <Grid gutter={0}>
        <Grid.Col span={isMobile ? 12 : 6}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Title ta="center">{t('welcomeToCitySense')}</Title>
            <Paper
              withBorder
              shadow="md"
              radius="md"
              p={30}
              w={isMobile ? '80%' : 500}
              h={isMobile ? 'auto' : 'auto'}
              style={{
                marginTop: isMobile ? 0 : 30,
                position: 'relative',
              }}
            >
              <LoadingOverlay
                visible={isLoading}
                zIndex={1000}
                overlayProps={{ radius: 'md', blur: 1 }}
                loaderProps={{
                  size: 'md',
                  type: 'oval',
                  speed: 1,
                  radius: 'md',
                }}
              />
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <TextInput
                  label={t('emailOrUsername')}
                  placeholder={t('emailOrUsernamePlaceholder')}
                  required
                  {...form.getInputProps('username')}
                />
                <PasswordInput
                  label={t('password')}
                  placeholder={t('yourPassword')}
                  required
                  mt="md"
                  {...form.getInputProps('password')}
                />
                <Button type="submit" fullWidth mt="xl">
                  {t('signIn')}
                </Button>
              </form>
            </Paper>{' '}
          </Box>
        </Grid.Col>
        <Grid.Col span={6} display={isMobile ? 'none' : 'block'}>
          <Image
            src="https://res.cloudinary.com/iterait/images/f_webp/v1684601658/VIVIDI_CITY_SENSE_STRAHOVSKY_STADION-11/VIVIDI_CITY_SENSE_STRAHOVSKY_STADION-11.png?_i=AA"
            alt="Login background"
            h="100vh"
            w="100%"
            fit="cover"
            style={{
              filter: 'brightness(0.5) saturate(0.8) blur(1px)',
            }}
          />
        </Grid.Col>
      </Grid>
    </Container>
  )
}
